/**
 * Generates initials from a given name.
 * If the name is a single word, it returns the first two letters in uppercase.
 * If the name contains multiple words, it returns the first letter of each word in uppercase.
 * If the first character is an emoji, it returns the first word of the name.
 * @param {string} name - The name from which to generate initials.
 * @returns {string} - The initials in uppercase or the first word if it's an emoji.
 */
export const getInitials = (name) => {
  // Check if the first character is in the emoji range
  const firstChar = name.codePointAt(0);
  if (firstChar > 127 && firstChar !== 8205) {
    if (name.split(" ").length > 1) {
      return name.split(" ")[0];
    }
    return name;
  }
  // If it's a single word, return the first two letters
  if (name.split(" ").length === 1) {
    return name.substring(0, 2).toUpperCase();
  }

  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

/**
 * Cleans a URL by removing HTTP/HTTPS protocol and www prefix.
 * @param {string} url - The URL to clean.
 * @returns {string} - The cleaned URL without protocol and www.
 */
export const cleanUrl = (url) => {
  if (typeof url !== "string" || !url) return "";
  // Remove the last slash if it exists
  if (url.endsWith("/")) {
    url = url.slice(0, -1);
  }

  // Remove protocol
  if (url.startsWith("https://")) {
    url = url.substring(8);
  } else if (url.startsWith("http://")) {
    url = url.substring(7);
  }

  // Remove www.
  if (url.startsWith("www.")) {
    url = url.substring(4);
  }

  return url;
};

/**
 * @deprecated Use cleanUrl instead
 * Filters out the HTTP/HTTPS protocol from a URL.
 * @param {string} url - The URL to filter.
 * @returns {string} - The URL without the HTTP/HTTPS protocol.
 */
export const filterHttpOut = cleanUrl;

/**
 * Extracts the domain from a URL, optionally filtering out the HTTP/HTTPS protocol.
 * @param {string} url - The URL from which to extract the domain.
 * @param {boolean} filterHttpOut - Whether to remove the HTTP/HTTPS protocol (default is true).
 * @returns {string} - The domain of the URL.
 */
export const domainOnly = (url, shouldFilterHttpOut = true) => {
  // Guard against a missing/non-string url (e.g. an as-yet-unsaved bookmark in
  // the edit form): callers expect a string back, not a throw.
  if (typeof url !== "string" || !url) return "";
  // Remove the last slash if it exists
  if (url.endsWith("/")) {
    url = url.slice(0, -1);
  }
  // Extract the domain from the URL
  const domain = url.split("/")[2] || url.split("/")[0];
  // If shouldFilterHttpOut is true, clean the domain
  if (shouldFilterHttpOut) {
    return cleanUrl(domain);
  }
  return domain;
};

/**
 * Extracts the path from a URL (everything after the domain).
 * @param {string} url - The URL from which to extract the path.
 * @returns {string} - The path of the URL, or empty string if no path exists.
 */
export const pathOnly = (url) => {
  if (typeof url !== "string" || !url) return "";
  try {
    // Create a URL object to properly parse the URL
    const urlObj = new URL(url.startsWith("http") ? url : `https://${url}`);
    // Get pathname and search params (query string)
    const path = urlObj.pathname + urlObj.search;
    // Return empty string for root paths, otherwise return the path
    return path === "/" ? "" : path;
  } catch (e) {
    // Fallback for invalid URLs
    const parts = url.split("/");
    if (parts.length > 3) {
      return "/" + parts.slice(3).join("/");
    }
    return "";
  }
};

/**
 * Picks the best cover image for a bookmark from its stored `images` array
 * (populated from og:image / content images by the backend MetadataService).
 * Prefers the og:image, then any usable url. Returns null when none exists so
 * callers can fall back to a favicon/gradient.
 * @param {Object} bookmark - The bookmark object.
 * @returns {string|null} - A cover image URL, or null.
 */
export const getCoverImage = (bookmark) => {
  const imgs = bookmark?.images;
  if (!imgs) return null;
  const arr = Array.isArray(imgs) ? imgs : [imgs];
  const pick =
    arr.find((i) => i && (i.type === "og:image" || typeof i === "string")) ||
    arr[0];
  if (!pick) return null;
  if (typeof pick === "string") return pick;
  return pick.url || pick.src || pick.image || null;
};

/**
 * Parses an error object and returns a user-friendly message.
 * @param {Object} error - The error object to parse
 * @returns {string} - A string containing the error message.
 */
export const parseError = (error) => {
  if (!error) {
    return "Something went wrong, please try again.";
  }

  // check if error is a stringified object
  if (typeof error === "string") {
    try {
      error = JSON.parse(error);
    } catch (e) {
      return error;
    }
  }

  // Handle object errors
  if (typeof error === "object") {
    // Return the message property if it exists
    if (error.message) {
      return error.message;
    }
    // Otherwise stringify the object
    return JSON.stringify(error);
  }

  // Return as string for any other type
  return String(error);
};

/**
 * Truncates a content to a specified maximum length, adding "..." if truncated.
 * @param {string} content - The title to truncate.
 * @param {number} maxLength - The maximum length of the title (default is 30).
 * @returns {string} - The truncated title or the original title if it is within the limit.
 */
export const truncateContent = (content, maxLength = 30) => {
  if (!content || content.length <= maxLength) return content;
  return content.substring(0, maxLength).trim() + "...";
};

/**
 * Formats a date into a human-readable string.
 * @param {string|Date} date - The date to format, can be a string or Date object.
 * @returns {string} - The formatted date string.
 */
export const formatDate = (date) => {
  if (!date) return "";
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Date(date).toLocaleDateString(undefined, options);
};

/**
 * Formats a date as a short relative time (e.g. "3d ago", "Yesterday", "Never").
 * @param {string|Date} date - The date to format.
 * @returns {string} - A short relative-time string.
 */
export const timeAgo = (date) => {
  if (!date) return "Never";
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

/**
 * Formats a price amount into a currency string.
 * @param {number} amount - The amount to format, in cents by default.
 * @param {string} currency - The currency code (default is "USD").
 * @param {boolean} isCents - Whether the amount is in cents (default is true).
 * @param {string} locale - The locale to use for formatting (optional, uses browser default if not provided).
 * @returns {string} - The formatted price string.
 */
export const formatPrice = (
  amount,
  currency = "USD",
  isCents = true,
  locale = null
) => {
  // Convert cents to whole currency units if needed
  const value = isCents ? amount / 100 : amount;

  // Currency configuration with fallback locales
  const currencyConfig = {
    USD: {
      symbol: "$",
      locale: "en-US",
      code: "USD",
    },
    BRL: {
      symbol: "R$",
      locale: "pt-BR",
      code: "BRL",
    },
  };

  // Get currency settings or default to USD
  const config = currencyConfig[currency] || currencyConfig.USD;

  // Use provided locale or the currency's default locale
  const useLocale = locale || config.locale;

  // Handle zero values specially - show currency symbol with 0
  if (value === 0) {
    return `${config.symbol}0`;
  }

  // Format the number with proper locale
  const formattedValue = new Intl.NumberFormat(useLocale, {
    style: "currency",
    currency: config.code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  return formattedValue;
};
